import { ElementProps } from '@/components/sections/builder/types';

import { POSITION, POSITION_DETAIL } from '@/components/sections/builder/constants/position';
import {
  ContentCardInfoType,
  ElementDataType,
} from '@/components/sections/builder/elements/body/slideshow/types';
import ImageSetting from '@/components/sections/builder/settings/image-setting';
import InputSlider from '@/components/sections/builder/settings/input-slider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn, removeHTMLTags } from '@/lib/utils';
import { useBuilderStore } from '@/stores/builder';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { shallow } from 'zustand/shallow';
import { Images } from 'lucide-react';
import InputLink from '@/components/sections/builder/settings/input-link';
import Link from 'next/link';
import useMediaQuery from '@/hooks/useMediaQuery';
import SchemeSetting from '@/components/sections/builder/settings/scheme-setting';
import TextEditorMinimal from '@/components/commons/minimal-tiptap/minimal-editor';
import DynamicImage from '@/components/commons/dynamic-image';
import InputSelect from '@/components/sections/builder/settings/input-select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';

const defaultData: ContentCardInfoType = {
  name: 'Slide',
  heading: 'Image slide',
  subheading: `Tell your brand's story through images`,
  buttonLabel: 'Button label',
  buttonLink: '#',
  headingSize: '0',
  isOutlineButton: false,
  desktopContentPosition: 'middleCenter',
  desktopContentAlignment: 'center',
  imgOverlayOpacity: 0,
  colorSchemeBackround: 'background1',
  mobileLayout: 'center',
  img: '/assets/temporary-banner.png',
  imgLink: '#',
  mobileMinWidth: 720,
};

export const ElementContent = ({ element }: ElementProps) => {
  const data = (element.data || defaultData) as ContentCardInfoType;
  const parentData = (element.parentData || defaultData) as ElementDataType;
  const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 0;

  const [mediaLoaded, setMediaLoaded] = useState(false);

  const imgUrl = useMediaQuery(data.mobileMinWidth) && data.mobileImg ? data.mobileImg : data.img;
  const sanitizedVideo = removeHTMLTags(data.video ?? '');
  const isImgLinkExternal = data.imgLink?.startsWith('http');
  const isButtonLinkExternal = data.buttonLink?.startsWith('http');

  const alignClass = useMemo(() => {
    if (data.desktopContentAlignment === 'left') return 'lg:text-left';
    if (data.desktopContentAlignment === 'right') return 'lg:text-right';
    return 'lg:text-center';
  }, [data.desktopContentAlignment]);

  const alignClassMobile = useMemo(() => {
    if (data.mobileLayout === 'left') return 'text-left';
    if (data.mobileLayout === 'right') return 'text-right';
    return 'text-center';
  }, [data.mobileLayout]);

  const position = POSITION_DETAIL[data?.desktopContentPosition] || POSITION_DETAIL['middleCenter'];

  useEffect(() => {
    const elements = document.getElementsByClassName(
      `slideshow-item-${element.parentId}`
    ) as HTMLCollectionOf<HTMLElement>;

    const wrappers = document.getElementsByClassName(`slideshow-wrapper-${element.parentId}`);

    for (let i = 0; i < wrappers.length; i++) {
      (wrappers[i] as HTMLElement).style.height = '';
    }

    if (parentData?.slideHeight !== 'adapt') {
      return;
    }

    if (!elements.length) return;

    const firstElement = Array.from(elements).find((el) => el.tagName !== 'VIDEO') || elements[0];

    const updateWrapperHeight = (naturalHeight: number, naturalWidth: number) => {
      for (let i = 0; i < wrappers.length; i++) {
        const wrapperWidth = wrappers[i]?.getBoundingClientRect()?.width || 0;
        const wrapperHeight = (naturalHeight / naturalWidth) * wrapperWidth;
        (wrappers[i] as HTMLElement).style.height = `${windowWidth <= 768 ? 390 : wrapperHeight}px`;
      }
    };

    if (firstElement.tagName === 'VIDEO') {
      const video = document.createElement('video');
      video.onloadedmetadata = () => {
        updateWrapperHeight(video.videoHeight, video.videoWidth);
        video.src = '';
      };
      video.src = firstElement.getAttribute('src') || '';
    } else {
      const img = new Image();
      img.onload = () => updateWrapperHeight(img.naturalHeight, img.naturalWidth);
      img.src = firstElement.getAttribute('src') || '';
    }
  }, [windowWidth, mediaLoaded, parentData.slideHeight, imgUrl, element.parentId]);

  const renderMedia = () => {
    if (sanitizedVideo) {
      return (
        <video
          key={sanitizedVideo}
          loop
          autoPlay
          muted
          src={sanitizedVideo}
          onLoad={() => setMediaLoaded(true)}
          className={cn(
            '!h-full !w-full object-cover object-center',
            `slideshow-item-${element.parentId}`
          )}
        />
      );
    }

    return (
      <div className='!relative !h-full !w-full overflow-hidden'>
        <DynamicImage
          key={imgUrl}
          className={clsx(
            '!relative !h-full !w-full object-cover object-center',
            `slideshow-item-${element.parentId}`,
            parentData.animation === 'ambientMovement' && 'animate-zoomSlideDown'
          )}
          width={1920}
          height={1080}
          sizes='100vw'
          src={imgUrl as string}
          alt='image'
          onLoadingComplete={() => setMediaLoaded(true)}
          style={{
            width: '100%',
            height: 'auto',
            opacity: `${100 - data.imgOverlayOpacity}%`,
          }}
        />
      </div>
    );
  };

  return (
    <>
      <div
        className={cn(
          'slideshow-content relative h-full w-full bg-transparent ',
          data.imgOverlayOpacity > 0 && 'p-0 lg:bg-black'
        )}
      >
        {/* ---------------- MEDIA ---------------- */}
        <div
          className={cn(
            'relative w-full object-cover object-center',
            parentData?.slideHeight !== 'adapt' &&
              parentData?.slideHeight === 'small' &&
              'h-[280px] md:h-[420px]',
            parentData?.slideHeight !== 'adapt' &&
              parentData?.slideHeight === 'medium' &&
              'h-[340px] md:h-[560px]',
            parentData?.slideHeight !== 'adapt' &&
              parentData?.slideHeight === 'large' &&
              'h-[390px] md:h-[720px]',
            parentData?.slideHeight === 'adapt' && 'h-auto',
            `slideshow-wrapper-${element.parentId}`
          )}
        >
          {data.imgLink ? (
            <Link
              href={
                isImgLinkExternal
                  ? data.imgLink || '#'
                  : `/${(data.imgLink || '').replace(/^\/+/, '')}`
              }
              target={isImgLinkExternal ? '_blank' : undefined}
              rel={isImgLinkExternal ? 'noopener noreferrer' : undefined}
              className='pointer-events-auto relative z-10 block h-full w-full'
              passHref
            >
              {renderMedia()}
            </Link>
          ) : (
            renderMedia()
          )}
        </div>

        {/* ---------------- CONTENT ---------------- */}
        <div
          className={cn(
            `slideshow-card-layout static left-0 top-0 w-full p-0 md:absolute md:h-full lg:p-6`,
            !parentData.showContentBelowImagesOnMobile && 'absolute h-full'
          )}
          style={{
            display: 'flex',
            justifyContent: position?.content || 'center',
            alignItems: position?.align || 'center',
          }}
        >
          <div className='slideshow-card-wrapper relative z-20 inline-block w-full md:w-fit'>
            <div
              className={cn(
                'slideshow-card bg-transparent p-6 py-10 lg:rounded lg:px-12 lg:py-16',
                alignClassMobile,
                alignClass
              )}
            >
              <h3
                className='leading-tight'
                style={{
                  fontSize: `${data.headingSize}px`,
                  color: data?.colorScheme?.text || '#52535B',
                }}
                dangerouslySetInnerHTML={{ __html: data.heading }}
              />
              <p
                className='mb-8 mt-4'
                style={{ color: data?.colorScheme?.text || '#52535B' }}
                dangerouslySetInnerHTML={{ __html: data.subheading }}
              />

              {data.buttonLabel && (
                <Link
                  href={
                    isButtonLinkExternal
                      ? data.buttonLink || '#'
                      : `/${(data.buttonLink || '').replace(/^\/+/, '')}`
                  }
                  target={isButtonLinkExternal ? '_blank' : undefined}
                  rel={isButtonLinkExternal ? 'noopener noreferrer' : undefined}
                >
                  <Button
                    className={cn(
                      'theme_button button',
                      data.isOutlineButton
                        ? 'bg-transparent text-[var(--btn-label-outline)] outline-[var(--btn-label-outline)]'
                        : 'bg-[var(--btn-bg)] text-[var(--text-color)] outline-transparent'
                    )}
                    style={
                      {
                        '--btn-color': data?.colorScheme?.btnSolidlabel || '#52535B',
                        '--btn-bg': data?.colorScheme?.btnSolidBackgroud || '#FFFFFF',
                        '--btn-label-outline': data?.colorScheme?.btnOutline || '#FFFFFF',
                      } as React.CSSProperties
                    }
                  >
                    {data.buttonLabel}
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
      <style>
        {`
          .slideshow-card-wrapper {
            display: flex;
          }
          .slideshow-card {
            max-width: 45.5rem;
            width: 100%;
          }
          .slideshow-image {
            display: block;
            max-width: 100%;
            position: absolute;
            top: 0;
            left: 0;
            height: 100%;
            width: 100%;
            object-fit: cover;
            object-position: center center;
            transition: opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          }
          .slideshow-card-layout {
            display: flex;
            justify-content: ${position.content};
            align-items: ${position.align};
          }
          @media (max-width: 768px) {
            ${
              parentData?.slideHeight === 'adapt'
                ? `
              .slideshow-wrapper-${element.parentId} {
                height: auto !important;
              }
              .slideshow-wrapper-${element.parentId} .slideshow-image {
                position: static;
                height: auto;
                width: 100%;
                object-fit: contain;
              }
            `
                : ''
            }
          }
        `}
      </style>
    </>
  );
};

export const ElementPreviewIcon = Images;

export function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as ContentCardInfoType;

  return (
    <div className='w-full max-w-md'>
      <h1 className='mb-4 text-sm font-bold'>Background Content</h1>
      <div className=''>Video url</div>
      <TextEditorMinimal
        onChange={(value) =>
          builder.setChildData(element.parentId as string, element.id, {
            video: value,
          })
        }
        value={data.video}
        className='w-full '
        output='text'
        editorProps={{
          attributes: {
            class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
          },
        }}
        editable
      />
      <div className='mb-4'>Supported formats: .mp4, .webm, .ogv</div>
      <div className='mb-2 mt-4'>Dekstop image </div>
      <ImageSetting
        value={data?.img}
        onChange={(val) => {
          builder.setChildData(element.parentId as string, element.id, {
            img: val,
          });
        }}
      />
      <div className='mb-2 mt-4'>Mobile image (optional)</div>
      <ImageSetting
        value={data?.mobileImg}
        onChange={(val) => {
          builder.setChildData(element.parentId as string, element.id, {
            mobileImg: val,
          });
        }}
      />
      <InputSlider
        label='Device under this size use mobile image'
        value={data.mobileMinWidth || 0}
        max={1440}
        min={0}
        rightContent={<div>px</div>}
        onChange={(val) =>
          builder.setChildData(element.parentId as string, element.id, {
            mobileMinWidth: val,
          })
        }
      />
      <div className='mb-2 mt-4 '>Image Link</div>
      <InputLink
        name='url_path_img'
        placeholder='Search or enter a link'
        onChange={(value) => {
          builder.setChildData(element.parentId as string, element.id, {
            imgLink: value,
          });
        }}
        value={data.imgLink as string}
      />
      <Separator className='my-4' />
      <h1 className='mb-4 text-sm font-bold'>Content Settings</h1>
      <div className=''>Heading</div>
      <TextEditorMinimal
        onChange={(value) =>
          builder.setChildData(element.parentId as string, element.id, {
            heading: value,
          })
        }
        value={data.heading}
        className='mb-4 w-full'
        output='html'
        editorProps={{
          attributes: {
            class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
          },
        }}
        editable
      />
      <div className='my-2 grid grid-cols-2 items-center gap-2'>
        <h4>Heading Size</h4>
        <InputSelect
          defaultValue={data.headingSize}
          options={[
            { label: 'Small', value: '20' },
            { label: 'Medium', value: '30' },
            { label: 'Large', value: '40' },
          ]}
          onChange={(v) =>
            builder.setChildData(element.parentId as string, element.id, {
              headingSize: v,
            })
          }
        />
      </div>
      <div className='mb-2 mt-4'>Description</div>
      <TextEditorMinimal
        onChange={(value) =>
          builder.setChildData(element.parentId as string, element.id, {
            subheading: value,
          })
        }
        value={data.subheading}
        className='w-full'
        output='html'
        editorProps={{
          attributes: {
            class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
          },
        }}
        editable
      />
      <div className='mb-2 mt-4 font-bold'>Button Style</div>
      <div className='mb-2 grid grid-cols-2 items-center gap-2'>
        <label className='text-xs'>Label</label>
        <Input
          className='w-full p-2 text-xs'
          name='labelViewCollection'
          onChange={(e) =>
            builder.setChildData(element.parentId as string, element.id, {
              buttonLabel: e.target.value,
            })
          }
          type='text'
          defaultValue={data.buttonLabel}
        />
      </div>
      <div className='mb-2 mt-4'>Button Link</div>
      <InputLink
        name='url_path'
        placeholder='Search or enter a link'
        onChange={(value) => {
          builder.setChildData(element.parentId as string, element.id, {
            buttonLink: value,
          });
        }}
        value={data.buttonLink as string}
      />
      <div className='my-4 flex items-center gap-2'>
        <label>Use outline button style</label>
        <Switch
          className='ml-auto'
          checked={data.isOutlineButton}
          onCheckedChange={(value) =>
            builder.setChildData(element.parentId as string, element.id, {
              isOutlineButton: !!value,
            })
          }
          aria-readonly
        />
      </div>
      <div className='my-4 grid grid-cols-2 items-center gap-2'>
        <h4>Desktop content position</h4>
        <InputSelect
          defaultValue={data.desktopContentPosition}
          options={POSITION.map((obj) => ({
            label: obj.label,
            value: obj.value,
          }))}
          onChange={(val) =>
            builder.setChildData(element.parentId as string, element.id, {
              desktopContentPosition: val,
            })
          }
        />
      </div>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4>Desktop content alignment</h4>
        <InputSelect
          defaultValue={data.desktopContentAlignment}
          options={[
            { label: 'Left', value: 'left' },
            { label: 'Center', value: 'center' },
            { label: 'Right', value: 'right' },
          ]}
          onChange={(v) =>
            builder.setChildData(element.parentId as string, element.id, {
              desktopContentAlignment: v,
            })
          }
        />
      </div>
      <div className='my-2 grid grid-cols-2 items-center gap-2'>
        <h4>Mobile content alignment</h4>
        <InputSelect
          defaultValue={data.mobileLayout}
          options={[
            { label: 'Left', value: 'left' },
            { label: 'Center', value: 'center' },
            { label: 'Right', value: 'right' },
          ]}
          onChange={(v) =>
            builder.setChildData(element.parentId as string, element.id, {
              mobileLayout: v,
            })
          }
        />
      </div>
      <InputSlider
        label='Image overlay opacity'
        value={data.imgOverlayOpacity}
        max={100}
        min={0}
        rightContent={<div>%</div>}
        onChange={(val) =>
          builder.setChildData(element.parentId as string, element.id, {
            imgOverlayOpacity: val,
          })
        }
      />
      <Separator className='my-4' />
      <h1 className='mb-3 text-sm font-bold'>Style</h1>
      <div className='mb-2 mt-4'>Color scheme</div>
      <SchemeSetting
        value={data.colorScheme}
        onChange={(theme) => {
          builder.setChildData(element.parentId as string, element.id, {
            colorScheme: theme,
          });
        }}
      />
      <div className='mt-1 text-xs'>
        To edit all your theme&apos;s colors, go to your color theme settings.
      </div>
    </div>
  );
}

const Slide = {
  type: 'slide',
  group: 'slideshow',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default Slide;
